import type { Assessment } from "../assessment-types";

export const TRAUMA_BONDS_ASSESSMENT: Assessment = {
  slug: "trauma-bonds",
  emoji: "🔗",
  title: "Trauma-Bond Patterns",
  tagline: "When leaving feels impossible",
  intro:
    "This reflects on patterns that can develop in relationships where care and harm are mixed together, making it hard to leave even when things feel painful. Answer honestly, this is just for your own understanding.",
  questions: [
    { id: "q1", text: "Do you find yourself making excuses for someone's hurtful behaviour?" },
    { id: "q2", text: "Do you feel drawn back to someone shortly after deciding to distance yourself from them?" },
    { id: "q3", text: "Do you focus on the good moments in a relationship to cope with the difficult ones?" },
    { id: "q4", text: "Do you feel like you cannot trust your own judgement about a relationship?" },
    { id: "q5", text: "Do you feel a sense of relief and gratitude after a conflict finally calms down?" },
    { id: "q6", text: "Do you find it hard to imagine your life without this person, even when it hurts you?" },
    { id: "q7", text: "Do you keep hoping things will go back to how they were at the start?" },
    { id: "q8", text: "Do you hide the full picture of the relationship from friends or family?" },
    { id: "q9", text: "Do you feel confused about what is actually true when you are with them?" },
    { id: "q10", text: "Do you blame yourself for problems that seem to happen again and again?" },
    { id: "q11", text: "Do you feel a pull to return even after promising yourself you would stay away?" },
    { id: "q12", text: "Do you notice your sense of self shrinking the longer the relationship continues?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest little sign of a trauma-bond pattern right now.",
      body:
        "The push and pull described here does not seem to be a strong feature of your relationships at the moment. It can still be useful to stay aware of these dynamics over time.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some early signs of a trauma-bond pattern.",
      body:
        "There may be moments where care and hurt feel tangled together, making it harder to see a relationship clearly. This confusion is a known effect of cycles of tension and relief, not a sign of weakness.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a recurring trauma-bond pattern.",
      body:
        "It sounds like leaving, or even fully seeing what is happening, can feel genuinely difficult quite often. This bond forms through repeated cycles of harm and comfort, and it can loosen with time, support and understanding.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong and frequent trauma-bond pattern.",
      body:
        "The pull to stay, to explain things away or to hope for change seems to show up regularly and strongly. This is a well-documented response to relationships that mix affection with harm, and it says nothing about your intelligence or strength.",
    },
  ],
  next: [
    { label: "Understanding trauma bonds", to: "/learn/trauma-bonds" },
    { label: "Trauma-informed relationships", to: "/learn/trauma-relationships" },
    { label: "Building healthier boundaries", to: "/learn/boundaries" },
    { label: "Reflect in the guided journal", to: "/guided-journal" },
  ],
};
